const registeredModules = {};
const { set } = require('lodash');
const merge = require('lodash/merge');
const uniq = require('lodash/uniq');
const { mergeSchemaFlags, isRequiredForAllCanonicalContexts } = require('./schemaFlags');
const writeRequirements = require('./writeRequirements');

/**
 * Registers an HNode type module in the global registry.
 * @param {string} hNodeTypeGroup - The group/category of the HNode type
 * @param {string} hNodeType - The specific HNode type name
 * @param {object} module - The module to register
 */
function registerHNodeType(hNodeTypeGroup, hNodeType, module) {
    set(registeredModules, [hNodeTypeGroup, hNodeType], module);
}

/**
 * Retrieves all HNode types within a group.
 * @param {string} hNodeTypeGroup - The group to retrieve
 * @returns {object|undefined} All registered types in the group
 */
function getHNodeTypeGroup(hNodeTypeGroup) {
    return registeredModules[hNodeTypeGroup];
}

/**
 * Retrieves a specific HNode type module.
 * @param {string} hNodeTypeGroup - The group containing the type
 * @param {string} hNodeType - The specific type to retrieve
 * @returns {object|undefined} The registered module
 */
function getHNodeType(hNodeTypeGroup, hNodeType) {
    return registeredModules[hNodeTypeGroup]?.[hNodeType];
}

/**
 * Merges a single access context method (get/post/patch/put/remove).
 * Uses OR semantics: if either has access, merged result has access.
 * Preserves observedLocation from the schema that grants access.
 */
function mergeAccessContextMethod(existingMethod = {}, incomingMethod = {}) {
    const existingValue = !!existingMethod?.value;
    const incomingValue = !!incomingMethod?.value;
    const value = existingValue || incomingValue;

    // Prefer observedLocation from whichever schema grants access
    const observedLocation = existingValue
        ? existingMethod?.observedLocation
        : incomingValue
          ? incomingMethod?.observedLocation
          : (existingMethod?.observedLocation ?? incomingMethod?.observedLocation);

    return {
        value,
        ...(observedLocation ? { observedLocation } : {})
    };
}

/**
 * Merges x-accessContext objects with OR semantics.
 * Multiple UI paths accumulate write capabilities.
 * @returns {object|undefined} Merged context or undefined if both inputs empty
 */
function mergeAccessContexts(existingContext, incomingContext) {
    if (!existingContext && !incomingContext) return undefined;
    if (!existingContext) return incomingContext;
    if (!incomingContext) return existingContext;

    const merged = {};
    ['get', 'post', 'patch', 'put', 'remove'].forEach(method => {
        const existingMethod = existingContext?.[method];
        const incomingMethod = incomingContext?.[method];
        if (!existingMethod && !incomingMethod) return;
        merged[method] = mergeAccessContextMethod(existingMethod, incomingMethod);
    });
    return merged;
}

/**
 * Route hints use the same shape as x-accessContext, but they mean something
 * narrower: "this UI block needs this HTTP method exposed." When two blocks
 * target the same relation, keep every true method.
 */
function mergeRouteAccessHints(existingHints, incomingHints) {
    return mergeAccessContexts(existingHints, incomingHints);
}

/**
 * Merges partial type definitions (property schemas keyed by name).
 * @param {object} target - Existing type definitions
 * @param {object} incoming - New definitions to merge
 * @returns {object} Merged definitions with schema flags properly combined
 */
function mergePartialTypeDefinitions(target = {}, incoming = {}, context = {}) {
    const result = { ...target };
    Object.entries(incoming || {}).forEach(([key, schema]) => {
        if (!result[key]) {
            result[key] = schema;
            return;
        }
        result[key] = mergeSchemaFlags(result[key], schema, { withinForm: !!context.writeForm });
    });
    return result;
}

/**
 * Resolves field-level flags for UI rendering.
 * @param {boolean} required - Whether field is required
 * @param {object} context - Context with readOnly and forAction
 * @returns {object} Resolved flags for field rendering
 */
function resolveFieldFlags(required, context = {}) {
    const isRequired = !!required && !context?.readOnly;
    const isEditable = !context?.readOnly;
    const forAction = context?.forAction; // 'create' or 'update' from DetailPane
    return {
        isRequired,
        isOptional: !isRequired,
        isEditable,
        isReadOnly: !isEditable,
        forAction,
        schemaOptions: {
            'x-required': isRequired,
            'x-optional': !isRequired,
            'x-editable': isEditable,
            'x-requiredActions': isRequired ? [forAction || '*'] : [],
            ...(forAction ? { 'x-forAction': forAction } : {})
        }
    };
}

/**
 * Merges two relation schemas with special handling for required arrays and action context.
 *
 * Key behaviors:
 * 1. Non-canonical actions (export, filter, list, nav, import) don't override canonical constraints
 * 2. Properties merged with x-forAction combination
 * 3. Required array only includes fields required for ALL action contexts
 * 4. anyOf/oneOf branches are combined
 *
 * @param {object} existing - Existing relation schema
 * @param {object} incoming - New schema to merge
 * @returns {object} Merged relation schema
 */
function mergeRelationSchemas(existing = {}, incoming = {}) {
    const merged = merge({}, existing, incoming);
    const requirements = writeRequirements.merge(existing['x-writeRequirements'], incoming['x-writeRequirements']);
    if (requirements) merged['x-writeRequirements'] = requirements;

    const accessContext = mergeAccessContexts(existing['x-accessContext'], incoming['x-accessContext']);
    if (accessContext) merged['x-accessContext'] = accessContext;
    // lodash merge would let the later schema win. OR the route hints instead,
    // so create and update buttons on the same relation can both survive.
    const routeAccessHints = mergeRouteAccessHints(existing['x-routeAccessHints'], incoming['x-routeAccessHints']);
    if (routeAccessHints) merged['x-routeAccessHints'] = routeAccessHints;

    // Merge properties with proper x-forAction handling
    if (existing.properties || incoming.properties) {
        merged.properties = {};
        const allPropNames = new Set([
            ...Object.keys(existing.properties || {}),
            ...Object.keys(incoming.properties || {})
        ]);

        allPropNames.forEach(propName => {
            const existingProp = existing.properties?.[propName];
            const incomingProp = incoming.properties?.[propName];

            if (!existingProp) {
                merged.properties[propName] = incomingProp;
            } else if (!incomingProp) {
                merged.properties[propName] = existingProp;
            } else {
                merged.properties[propName] = mergeSchemaFlags(existingProp, incomingProp);
            }
        });
        if (requirements) {
            merged.properties = writeRequirements.sharedArrayConstraints(merged.properties, requirements);
        }
    }

    // Recompute required array from merged property metadata.
    // Keep legacy required[] candidates as conservative fallback if schema metadata is missing.
    const mergedProperties = merged.properties || {};
    const requiredCandidates = new Set([
        ...Object.keys(mergedProperties),
        ...(Array.isArray(existing.required) ? existing.required : []),
        ...(Array.isArray(incoming.required) ? incoming.required : [])
    ]);

    const requiredForAll = requirements
        ? writeRequirements.universal(requirements)
        : Array.from(requiredCandidates).filter(fieldName => {
              const prop = mergedProperties[fieldName];
              if (!prop) {
                  // Fallback for legacy definitions that still rely only on required[] arrays.
                  return (
                      (Array.isArray(existing.required) && existing.required.includes(fieldName)) ||
                      (Array.isArray(incoming.required) && incoming.required.includes(fieldName))
                  );
              }
              return isRequiredForAllCanonicalContexts(fieldName, prop);
          });

    if (requiredForAll.length) {
        merged.required = uniq(requiredForAll).sort();
    } else {
        delete merged.required;
    }

    // Combine anyOf/oneOf branches from both schemas
    if (existing.anyOf || incoming.anyOf) {
        const existingBranches = existing.anyOf || (existing.type ? [existing] : []);
        const incomingBranches = incoming.anyOf || (incoming.type ? [incoming] : []);
        if (existingBranches.length && incomingBranches.length) {
            merged.anyOf = [...existingBranches, ...incomingBranches];
            delete merged.type;
            delete merged.properties;
            delete merged.required;
        }
    }

    return merged;
}

/**
 * Merges two subDataDictionary objects (namespace -> relation -> schema).
 * Uses mergeRelationSchemas for relation-level merging.
 *
 * @param {object} target - Existing dictionary {namespace: {relation: schema}}
 * @param {object} source - Dictionary to merge in
 * @returns {object} Merged dictionary
 */
function mergeSubDataDictionaries(target = {}, source = {}) {
    const result = { ...target };

    Object.entries(source).forEach(([namespace, relations]) => {
        if (!result[namespace]) {
            result[namespace] = { ...relations };
            return;
        }

        Object.entries(relations || {}).forEach(([relation, schema]) => {
            if (!result[namespace][relation]) {
                result[namespace][relation] = schema;
            } else {
                result[namespace][relation] = mergeRelationSchemas(
                    result[namespace][relation],
                    schema,
                    `${namespace}.${relation}`
                );
            }
        });
    });

    return result;
}

module.exports = {
    registerHNodeType,
    getHNodeTypeGroup,
    getHNodeType,
    resolveFieldFlags,
    mergePartialTypeDefinitions,
    mergeRelationSchemas,
    mergeSubDataDictionaries
};
